import {
  CONTACT_TYPES,
  EMAIL_LABELS,
  PHONE_LABELS,
  RELATION_KINDS,
  formatDate,
  formatDateTime,
  formatDui,
  formatPhone,
  type RelationKind,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, GitMerge, Mail, MessageCircle, Pencil, Phone, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { ActivitiesCard } from '../../components/activities/activities-card.tsx';
import { ActivityDialog } from '../../components/activities/activity-dialog.tsx';
import { ContactDealsCard } from '../../components/contacts/contact-deals-card.tsx';
import { OwnedPropertiesCard } from '../../components/contacts/owned-properties-card.tsx';
import { PrivacyBanners, PrivacyCard } from '../../components/contacts/privacy-card.tsx';
import { TagChip } from '../../components/contacts/tag-chip.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Combobox } from '../../components/ui/combobox.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import {
  contactKeys,
  useContact,
  useContacts,
  useTags,
  whatsappLink,
  type ContactDetail,
} from '../../lib/contacts-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

export function ContactDetailPage() {
  const { id = '' } = useParams();
  const contact = useContact(id);
  if (contact.isPending) return <Loading />;
  if (contact.error) {
    return (contact.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(contact.error)}</Alert>
    );
  }
  return <ContactView contact={contact.data} />;
}

function ContactView({ contact }: { contact: ContactDetail }) {
  const { data: session } = useSession();
  const timeZone = session?.tenant.timezone;
  const [merging, setMerging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // After calling, writing or emailing: offer to log it (HU-AGE-06).
  const [contacted, setContacted] = useState<'call' | 'whatsapp' | 'email' | null>(null);
  const [logging, setLogging] = useState<'call' | 'whatsapp' | 'email' | null>(null);
  const canLog = useCan('records.edit') && contact.privacy.limited === null;
  const primaryPhone = contact.phones[0];
  const whatsapp = contact.phones.find((p) => p.whatsapp);
  const primaryEmail = contact.emails[0];

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/contactos" className="text-sm text-primary hover:underline">
          ← Contactos
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">{contact.name}</h1>
            <div className="flex flex-wrap gap-1.5">
              <Badge>{contact.kind === 'company' ? 'Empresa' : 'Persona'}</Badge>
              {contact.types.map((t) => (
                <Badge key={t} tone="info">
                  {CONTACT_TYPES[t]}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {contact.can.edit && (
              <Link
                to={`/contactos/${contact.id}/editar`}
                className={buttonVariants({ variant: 'secondary' })}
              >
                <Pencil /> Editar
              </Link>
            )}
            {contact.can.edit && (
              <Button
                variant="ghost"
                onClick={() => {
                  setMerging(true);
                }}
              >
                <GitMerge /> Unir con otro
              </Button>
            )}
            {contact.can.delete && (
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleting(true);
                }}
              >
                <Trash2 /> Eliminar
              </Button>
            )}
          </div>
        </div>
        <PrivacyBanners contact={contact} />
        {/* One tap from the phone: call, WhatsApp, email. */}
        <div className="grid grid-cols-3 gap-2 sm:flex">
          {primaryPhone && (
            <a
              href={`tel:${primaryPhone.number}`}
              className={buttonVariants({ variant: 'brand' })}
              onClick={() => {
                setContacted('call');
              }}
            >
              <Phone /> Llamar
            </a>
          )}
          {whatsapp && (
            <a
              href={whatsappLink(whatsapp.number)}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'secondary' })}
              onClick={() => {
                setContacted('whatsapp');
              }}
            >
              <MessageCircle /> WhatsApp
            </a>
          )}
          {primaryEmail && (
            <a
              href={`mailto:${primaryEmail.address}`}
              className={buttonVariants({ variant: 'secondary' })}
              onClick={() => {
                setContacted('email');
              }}
            >
              <Mail /> Correo
            </a>
          )}
        </div>
        {contacted && canLog && (
          <Alert tone="info" title="¿Registramos el contacto en la agenda?">
            <div className="mt-1 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setLogging(contacted);
                  setContacted(null);
                }}
              >
                Registrar {LOG_LABELS[contacted]}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setContacted(null);
                }}
              >
                Ahora no
              </Button>
            </div>
          </Alert>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="space-y-3">
          <h2 className="font-semibold">Contacto</h2>
          <dl className="space-y-2 text-sm">
            {contact.phones.map((p) => (
              <div key={p.number} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">
                  {PHONE_LABELS[p.label]}
                  {p.whatsapp ? ' · WhatsApp' : ''}
                </dt>
                <dd>
                  <a href={`tel:${p.number}`} className="text-primary hover:underline">
                    {formatPhone(p.number)}
                  </a>
                </dd>
              </div>
            ))}
            {contact.emails.map((e) => (
              <div key={e.address} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{EMAIL_LABELS[e.label]}</dt>
                <dd className="truncate">
                  <a href={`mailto:${e.address}`} className="text-primary hover:underline">
                    {e.address}
                  </a>
                </dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card className="space-y-3">
          <h2 className="font-semibold">Cartera</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Distrito</dt>
            <dd>
              {contact.district
                ? `${contact.district.name} · ${contact.district.municipality}`
                : '—'}
            </dd>
            <dt className="text-muted-foreground">Canal</dt>
            <dd>{contact.channel?.name ?? '—'}</dd>
            <dt className="text-muted-foreground">Asignado a</dt>
            <dd>{contact.assignedTo.name}</dd>
            <dt className="text-muted-foreground">Alta</dt>
            <dd>
              {formatDate(contact.createdAt, timeZone)}
              {contact.createdBy ? ` · ${contact.createdBy}` : ''}
            </dd>
            <dt className="text-muted-foreground">Actualizado</dt>
            <dd>{formatDateTime(contact.updatedAt, timeZone)}</dd>
          </dl>
        </Card>

        <SensitiveCard contact={contact} />
        <RelationsCard contact={contact} />
        <TagsCard contact={contact} />
        <PrivacyCard contact={contact} />
        <div className="lg:col-span-2">
          <ActivitiesCard
            link="contact"
            id={contact.id}
            draft={{ contact: { id: contact.id, label: contact.name } }}
          />
        </div>
        <ContactDealsCard contactId={contact.id} />
        <OwnedPropertiesCard contactId={contact.id} />

        <Card className="space-y-2">
          <h2 className="font-semibold">Notas privadas</h2>
          <p className="text-sm whitespace-pre-line text-muted-foreground">
            {contact.privateNotes ?? 'Sin notas.'}
          </p>
        </Card>
      </div>

      {merging && (
        <MergeDialog
          contact={contact}
          onClose={() => {
            setMerging(false);
          }}
        />
      )}
      {logging && (
        <ActivityDialog
          draft={{
            kind: logging,
            logDone: true,
            contact: { id: contact.id, label: contact.name },
            time: currentTime(timeZone),
          }}
          onClose={() => {
            setLogging(null);
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          contact={contact}
          onClose={() => {
            setDeleting(false);
          }}
        />
      )}
    </div>
  );
}

const LOG_LABELS = { call: 'la llamada', whatsapp: 'el WhatsApp', email: 'el correo' } as const;

/** "14:05" now, in the agency's zone. */
function currentTime(timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date());
}

function SensitiveCard({ contact }: { contact: ContactDetail }) {
  const notify = useToast();
  const [values, setValues] = useState<{
    nationalId: string | null;
    address: string | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      setValues(await api(`/contacts/${contact.id}/reveal`, { method: 'POST' }));
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };
  const nothing = !contact.hasNationalId && !contact.hasAddress;
  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold">Datos sensibles</h2>
        {!nothing && contact.can.revealSensitive && !values && (
          <Button size="sm" variant="secondary" loading={busy} onClick={() => void reveal()}>
            <Eye /> Mostrar
          </Button>
        )}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">DUI</dt>
        <dd>
          {values?.nationalId
            ? formatDui(values.nationalId)
            : contact.hasNationalId
              ? 'Registrado (oculto)'
              : 'No registrado'}
        </dd>
        <dt className="text-muted-foreground">Dirección exacta</dt>
        <dd>{values?.address ?? (contact.hasAddress ? 'Registrada (oculta)' : 'No registrada')}</dd>
      </dl>
      {values && (
        <p className="text-xs text-muted-foreground">
          Esta consulta quedó registrada en la bitácora.
        </p>
      )}
      {!nothing && !contact.can.revealSensitive && (
        <p className="text-xs text-muted-foreground">Tu rol no permite ver estos datos.</p>
      )}
    </Card>
  );
}

function useRefresh(id: string) {
  const client = useQueryClient();
  return (detail?: ContactDetail) => {
    if (detail) client.setQueryData(contactKeys.detail(id), detail);
    return client.invalidateQueries({ queryKey: contactKeys.all });
  };
}

function ContactSearch({
  excludeId,
  onPick,
  label,
}: {
  excludeId: string;
  onPick: (c: { id: string; name: string }) => void;
  label: string;
}) {
  const [query, setQuery] = useState('');
  const text = query.trim();
  const results = useContacts(text ? { q: text } : {}, text.length > 0);
  const options = query.trim()
    ? (results.data?.items ?? [])
        .filter((c) => c.id !== excludeId)
        .map((c) => ({
          id: c.id,
          label: c.name,
          detail: [c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · '),
        }))
    : [];
  return (
    <Field label={label}>
      {(a11y) => (
        <Combobox
          id={a11y.id}
          query={query}
          onQueryChange={setQuery}
          options={options}
          loading={results.isFetching}
          placeholder="Nombre, teléfono o correo"
          onSelect={(option) => {
            onPick({ id: option.id, name: option.label });
            setQuery(option.label);
          }}
        />
      )}
    </Field>
  );
}

function RelationsCard({ contact }: { contact: ContactDetail }) {
  const refresh = useRefresh(contact.id);
  const notify = useToast();
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<RelationKind>('spouse');
  const [related, setRelated] = useState<{ id: string; name: string } | null>(null);

  const add = async () => {
    if (!related) return;
    try {
      await refresh(
        await api<ContactDetail>(`/contacts/${contact.id}/relations`, {
          method: 'POST',
          body: { relatedContactId: related.id, kind },
        }),
      );
      setAdding(false);
      setRelated(null);
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const remove = async (relationId: string) => {
    try {
      await refresh(
        await api<ContactDetail>(`/contacts/${contact.id}/relations/${relationId}`, {
          method: 'DELETE',
        }),
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold">Relaciones</h2>
        {contact.can.edit && !adding && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAdding(true);
            }}
          >
            Agregar
          </Button>
        )}
      </div>
      {contact.relations.length === 0 && !adding && (
        <p className="text-sm text-muted-foreground">Sin relaciones.</p>
      )}
      <ul className="space-y-1 text-sm">
        {contact.relations.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2">
            <span>
              <span className="text-muted-foreground">
                {r.outgoing ? RELATION_KINDS[r.kind].label : RELATION_KINDS[r.kind].inverse}:{' '}
              </span>
              <Link to={`/contactos/${r.contact.id}`} className="text-primary hover:underline">
                {r.contact.name}
              </Link>
            </span>
            {contact.can.edit && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Quitar relación con ${r.contact.name}`}
                onClick={() => void remove(r.id)}
              >
                Quitar
              </Button>
            )}
          </li>
        ))}
      </ul>
      {adding && (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <Field label="Relación">
            {(a11y) => (
              <Select
                {...a11y}
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as RelationKind);
                }}
              >
                {(Object.entries(RELATION_KINDS) as [RelationKind, { label: string }][]).map(
                  ([value, { label }]) => (
                    <option
                      key={value}
                      value={value}
                    >{`${contact.name} es: ${label.toLowerCase()}`}</option>
                  ),
                )}
              </Select>
            )}
          </Field>
          <ContactSearch excludeId={contact.id} onPick={setRelated} label="Contacto relacionado" />
          <div className="flex gap-2">
            <Button size="sm" disabled={!related} onClick={() => void add()}>
              Guardar relación
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TagsCard({ contact }: { contact: ContactDetail }) {
  const tags = useTags();
  const refresh = useRefresh(contact.id);
  const notify = useToast();
  const [newTag, setNewTag] = useState('');
  const apply = async (tagId: string, action: 'add' | 'remove') => {
    try {
      await api('/contacts/tags/bulk', {
        method: 'POST',
        body: { contactIds: [contact.id], tagId, action },
      });
      await refresh();
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const create = async () => {
    try {
      const tag = await api<{ id: string }>('/tags', { method: 'POST', body: { name: newTag } });
      setNewTag('');
      await apply(tag.id, 'add');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const available = (tags.data ?? []).filter((t) => !contact.tags.some((c) => c.id === t.id));
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Etiquetas</h2>
      <div className="flex flex-wrap gap-1.5">
        {contact.tags.length === 0 && (
          <p className="text-sm text-muted-foreground">Sin etiquetas.</p>
        )}
        {contact.tags.map((t) => (
          <TagChip
            key={t.id}
            name={t.name}
            color={t.color}
            {...(contact.can.edit ? { onRemove: () => void apply(t.id, 'remove') } : {})}
          />
        ))}
      </div>
      {contact.can.edit && (
        <div className="space-y-2">
          <Select
            aria-label="Agregar etiqueta"
            value=""
            onChange={(e) => {
              if (e.target.value) void apply(e.target.value, 'add');
            }}
          >
            <option value="">Agregar etiqueta…</option>
            {available.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newTag.trim()) void create();
            }}
          >
            <input
              aria-label="Etiqueta nueva"
              placeholder="Etiqueta nueva"
              value={newTag}
              onChange={(e) => {
                setNewTag(e.target.value);
              }}
              className="min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base sm:text-sm"
            />
            <Button type="submit" variant="secondary" disabled={!newTag.trim()}>
              Crear
            </Button>
          </form>
        </div>
      )}
    </Card>
  );
}

function MergeDialog({ contact, onClose }: { contact: ContactDetail; onClose: () => void }) {
  const refresh = useRefresh(contact.id);
  const notify = useToast();
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null);
  const other = useContactOptional(duplicate?.id);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    if (!duplicate) return;
    setBusy(true);
    try {
      await refresh(
        await api<ContactDetail>(`/contacts/${contact.id}/merge`, {
          method: 'POST',
          body: { duplicateId: duplicate.id },
        }),
      );
      notify(`Se unió «${duplicate.name}» con «${contact.name}».`, 'success');
      onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };
  const summary = (c: ContactDetail) =>
    [
      ...c.phones.map((p) => formatPhone(p.number)),
      ...c.emails.map((e) => e.address),
      ...c.types.map((t) => CONTACT_TYPES[t]),
      c.hasNationalId ? 'DUI registrado' : null,
      `Asignado a ${c.assignedTo.name}`,
    ].filter(Boolean);
  return (
    <Dialog
      open
      title={`Unir un duplicado con ${contact.name}`}
      description="Todo lo del duplicado (teléfonos, correos, papeles, relaciones y etiquetas) pasa a este contacto. El duplicado se elimina y no se puede restaurar."
      onClose={onClose}
      className="sm:w-[min(44rem,calc(100vw-2rem))]"
    >
      <div className="space-y-4">
        <ContactSearch excludeId={contact.id} onPick={setDuplicate} label="Duplicado" />
        {duplicate && other.data && (
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { title: 'Se conserva', c: contact },
              { title: 'Se une y se elimina', c: other.data },
            ].map(({ title, c }) => (
              <div key={c.id} className="rounded-lg border border-border p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{title}</p>
                <p className="font-medium">{c.name}</p>
                <ul className="mt-1 text-sm text-muted-foreground">
                  {summary(c).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={!duplicate}
            loading={busy}
            onClick={() => void merge()}
          >
            Unir contactos
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function useContactOptional(id: string | undefined) {
  return useContact(id ?? '__none__', id !== undefined);
}

function DeleteDialog({ contact, onClose }: { contact: ContactDetail; onClose: () => void }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/contacts/${contact.id}`, { method: 'DELETE' });
      await client.invalidateQueries({ queryKey: contactKeys.all });
      notify(
        `«${contact.name}» se envió a la papelera. Puedes restaurarlo durante 30 días.`,
        'success',
      );
      await navigate('/contactos');
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title={`Eliminar a ${contact.name}`}
      description="Va a la papelera: se puede restaurar durante 30 días; después se borra para siempre."
      onClose={onClose}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={busy} onClick={() => void remove()}>
          Enviar a la papelera
        </Button>
      </div>
    </Dialog>
  );
}
